import { colors } from '../../styles/theme'

export interface PreviewDebounceBarProps {
  /** True while edits are settling — i.e. a preview is queued but not yet sent. */
  pending: boolean
  /**
   * Changes on every edit. Used as the element `key`, which remounts the bar and therefore
   * restarts its CSS animation from full. Driving the animation off a key rather than off state
   * is what keeps the reset instant: there is no frame where the old width is still animating.
   */
  runKey: number
  /** Must match the debounce delay, or the bar empties at the wrong moment. */
  durationMs: number
}

/**
 * A countdown for the debounced live preview: full immediately after a keystroke, draining to
 * empty over the debounce window, and the render fires as it empties.
 *
 * <para>
 * The preview used to be sent on <em>every keystroke</em> — the payload was debounced but the
 * document edit was not — so a full PDF render ran per character typed. Debouncing alone would
 * have made the editor feel unresponsive in the other direction ("did it notice I typed?"), which
 * is what the bar answers: it shows that the edit registered and that a render is coming.
 * </para>
 *
 * <para>
 * The drain is a CSS animation rather than a JS timer, so it stays smooth while the main thread is
 * busy — which, in an editor that reserialises the document on each keystroke, it frequently is.
 * A timer-driven bar would stutter exactly when the user is typing fastest.
 * </para>
 */
export default function PreviewDebounceBar({ pending, runKey, durationMs }: PreviewDebounceBarProps) {
  return (
    <div
      aria-hidden="true"
      style={{ height: 2, background: 'transparent', overflow: 'hidden', flex: '0 0 auto' }}
    >
      {pending && (
        <div
          key={runKey}
          data-testid="preview-debounce-bar"
          style={{
            height: '100%',
            background: colors.primary,
            transformOrigin: 'left center',
            animation: `cdm-preview-debounce ${durationMs}ms linear forwards`,
          }}
        />
      )}
    </div>
  )
}

/** Injected once by the editor page; kept beside the component so the two cannot drift apart. */
export const PREVIEW_DEBOUNCE_KEYFRAMES = `
  @keyframes cdm-preview-debounce {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }
`
